import { DashboardContent } from 'src/layouts/dashboard';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Grid, Box, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper, Chip, Checkbox} from '@mui/material';
import { useEffect, useState } from 'react';
import GetAppIcon from '@mui/icons-material/GetApp';
import PaidIcon from '@mui/icons-material/Paid';
import useLeaderboard from './useLeaderboard';
import useConfig from '../configs/useConfigs';
import { downloadLeaderboardCSV, issuedLeaderboard } from 'src/pages/admin/services/whitelistService';

export interface LeaderboardEntry {
  userId: string;
  rank: number;
  walletAddress: string;
  cumulativeVolumeUsdc: number;
  computedReward: number;
  payoutStatus: string;
  proxyWalletAddress: string
}

export interface LeaderboardSummary {
  totalParticipants: number;
  totalPoolUsdt: string;
  issuedUsdt: string;
  remainingUsdt: string;
  eventEndAt: number;
}

const View = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { retrieve, records: entries, retrieveSummary, summary } = useLeaderboard();
  const { retrieve: retrieveConfigs, configsData } = useConfig();

  useEffect(() => {
    retrieve();
    retrieveSummary();
    retrieveConfigs();
  }, [retrieve, retrieveSummary, retrieveConfigs]);

  const formatVolume = (vol: number) => {
    if (!vol) return;
    return vol.toLocaleString();
  };

  const unpaidEntries = entries.filter((e) => e.payoutStatus !== 'PAID');

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(unpaidEntries.map((e) => e.userId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBatchDistribute = async () => {
    try {
      const response = await issuedLeaderboard(Array.from(selectedIds));
      if (response.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
      setSelectedIds(new Set());
      await retrieve();
    } catch (error: any) {
      console.log('error: ', error);
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await downloadLeaderboardCSV();

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'leaderboard_rewards.csv');

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download CSV:', error);
    }
  };

  const selectedCount = selectedIds.size;
  // const isBatchIssueEnabled = configsData && configsData.endAt  && Date.now() > configsData.endAt;
  const isBatchIssueEnabled = true

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-leaderboard" action="read" fallback={<ErrorPage403 />}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="medium">
          {translate('whitelist.leaderboard.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('whitelist.leaderboard.description')}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.leaderboard.participants')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.totalParticipants : 0} {translate('whitelist.personUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.leaderboard.totalRewardPool')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {summary ? summary.totalPoolUsdt : 0} USDT
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.leaderboard.issued')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {summary ? summary.issuedUsdt : 0} USDT
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.leaderboard.remaining')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.remainingUsdt : 0} USDT
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper
          sx={{
            p: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2">
            {translate('whitelist.leaderboard.itemSelected', { selectedCount: selectedCount })}{' '}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <PermissionGuard resource="whitelist-leaderboard" action="distribute" fallback={null}>
              <Button
                variant="contained"
                startIcon={<PaidIcon />}
                onClick={handleBatchDistribute}
                disabled={selectedCount === 0 || !isBatchIssueEnabled}
              >
                {translate('whitelist.leaderboard.batchIssue')} ({selectedCount})
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="whitelist-leaderboard" action="export" fallback={null}>
              <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportToCSV}>
                {translate('whitelist.exportCSV')}
              </Button>
            </PermissionGuard>
          </Box>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#fff',
                  '& th': {
                    backgroundColor: '#fff',
                    fontWeight: 600,
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedCount > 0 && selectedCount < unpaidEntries.length}
                    checked={selectedCount === unpaidEntries.length && unpaidEntries.length > 0}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>{translate('whitelist.leaderboard.rank')}</TableCell>
                <TableCell>{translate('whitelist.leaderboard.address')}</TableCell>
                <TableCell align="right">{translate('whitelist.leaderboard.volume')}</TableCell>
                <TableCell align="right">{translate('whitelist.leaderboard.reward')}</TableCell>
                <TableCell align="center">{translate('whitelist.leaderboard.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.userId} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(entry.userId)}
                      disabled={entry.payoutStatus === 'PAID'}
                      onChange={() => handleSelectOne(entry.userId)}
                    />
                  </TableCell>
                  <TableCell>#{entry.rank}</TableCell>
                  <TableCell>{entry.proxyWalletAddress}</TableCell>
                  <TableCell align="right">{formatVolume(entry.cumulativeVolumeUsdc)}</TableCell>
                  <TableCell align="right">{entry.computedReward.toLocaleString()} USDT</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={
                        entry.payoutStatus === 'PAID'
                          ? translate('whitelist.leaderboard.issued')
                          : translate('whitelist.leaderboard.unissued')
                      }
                      color={entry.payoutStatus === 'PAID' ? 'success' : 'default'}
                      size="small"
                      variant={entry.payoutStatus === 'PAID' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
